import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BudgetForm, {
  BudgetFlatFormValues,
} from 'src/components/BudgetForm/BudgetForm'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import {
  BudgetRequestListModel,
  BudgetRequestStatus,
} from 'src/models/budgetRequest.models'
import { CreditCardType } from 'src/models/creditCard.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import { createBudgetUrl } from 'src/navigation/urls'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { handlePageLocation } from 'src/utils/analytics.utils'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface UpdateBudgetRequestRecurringContentProps {
  budgetRequest: BudgetRequestListModel
  recipient: UserListModel
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  rcnOptions: CreditCardOption[]
  puttingBudgetRequest: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  initialFlatFields?: Partial<BudgetFlatFormValues>
  setScreen: (val: BudgetScreen) => void
  putBudgetRequest: (
    body: Partial<PostCreditCardRequest>,
    status: BudgetRequestStatus,
  ) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  isFormDisabled: boolean
  requesting?: boolean
  approving?: boolean
  approver: UserListModel
  showCurrencyError: boolean
}

const UpdateBudgetRequestRecurringContent: React.FC<
  UpdateBudgetRequestRecurringContentProps
> = (props) => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const user = useCurrentUser()
  const budgetRecurringActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetRecurringActive,
  )
  const showRequestBudget = props.requesting && !props.approving
  const showApproveBudgetRequest = props.approving

  const onSuccessfulSubmit = (
    flatFields: BudgetFlatFormValues,
    recurrenceFields: RecurrenceFormValues,
    status: BudgetRequestStatus,
  ): void => {
    const shouldResetCount =
      recurrenceFields.period !== props.budgetRequest.recurrence?.period ||
      recurrenceFields.interval !== props.budgetRequest.recurrence?.interval ||
      (recurrenceFields.period === RecurrencePeriod.Weekly &&
        recurrenceFields.byWeekDay !==
          props.budgetRequest.recurrence?.byWeekDay) ||
      (recurrenceFields.period === RecurrencePeriod.Monthly &&
        recurrenceFields.byMonthDay !==
          props.budgetRequest.recurrence?.byMonthDay) ||
      (recurrenceFields.period === RecurrencePeriod.Yearly &&
        recurrenceFields.byMonthDay !==
          props.budgetRequest.recurrence?.byYearDay) ||
      recurrenceFields.count !==
        props.budgetRequest.recurrence?.remainingCount ||
      recurrenceFields.untilMoment?.format('YYYY-MM-DD') !==
        props.budgetRequest.recurrence
          ?.untilTimezonedMoment(props.budgetRequest.timezone)
          ?.format('YYYY-MM-DD') ||
      recurrenceFields.terminator !== props.budgetRequest.recurrence?.terminator
    const budgetRequestBodyRejected = {
      approverEmail: props.approver.email,
      currency: flatFields.currency,
      emails: flatFields.emails,
      displayName: flatFields.displayName,
      issuingLimitCents:
        flatFields.issuingLimitDollars !== undefined && !flatFields.isUnlimited
          ? toCents(flatFields.issuingLimitDollars)
          : undefined,
      type: CreditCardType.Delegate,
      recurs: true,
      recurrence: {
        balanceCents:
          recurrenceFields.amountDollars !== undefined
            ? toCents(recurrenceFields.amountDollars)
            : 0,
        period: recurrenceFields.period,
        interval: recurrenceFields.interval,
        terminator: recurrenceFields.terminator,
        count:
          recurrenceFields.terminator === RecurrenceTerminator.Count
            ? shouldResetCount
              ? recurrenceFields.count
              : props.budgetRequest.recurrence?.count
            : undefined,
        until:
          recurrenceFields.terminator === RecurrenceTerminator.Date
            ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
            : undefined,
        byWeekDay:
          recurrenceFields.period === RecurrencePeriod.Weekly
            ? recurrenceFields.byWeekDay
            : undefined,
        byMonthDay:
          recurrenceFields.period === RecurrencePeriod.Monthly
            ? recurrenceFields.byMonthDay
            : undefined,
        byYearDay:
          recurrenceFields.period === RecurrencePeriod.Yearly
            ? recurrenceFields.byYearDay
            : undefined,
        resetCount:
          recurrenceFields.terminator === RecurrenceTerminator.Count
            ? shouldResetCount
            : false,
      },
      notes: flatFields.notes,
    }

    const budgetRequestBody = {
      ...budgetRequestBodyRejected,
      parentCreditCardId: flatFields.creditCardId,
    }

    props.putBudgetRequest(
      status === BudgetRequestStatus.Rejected
        ? budgetRequestBodyRejected
        : budgetRequestBody,
      status,
    )
    /* Old analytics event - tbd if / when we delete */
    if (user !== undefined) {
      analytics?.track(
        handlePageLocation(calculatedBackgroundLocation) +
          ' - create standard budget - button press',
        {
          interaction: 'button press',
          intent: 'create a budget',
          typeOfBudget: 'Standard',
          budgetName: flatFields.displayName,
          recipientCount:
            flatFields.emails !== undefined
              ? flatFields.emails.length
              : flatFields.email !== undefined
                ? 1
                : 0,
          recipient:
            flatFields.emails !== undefined
              ? flatFields.emails
              : flatFields.email !== undefined
                ? flatFields.email
                : undefined,
          isRecipientMe: flatFields.emails
            ? flatFields.emails.includes(user.email)
            : flatFields.email !== undefined
              ? flatFields.email === user.email
              : undefined,
          sourceAccount:
            flatFields.parentCreditCardId !== undefined
              ? creditCards[flatFields.parentCreditCardId].displayName
              : undefined,
          budgetLimit: flatFields.isUnlimited
            ? 'Unlimited'
            : '$' + flatFields.issuingLimitDollars,
          activeUntil: flatFields.isForever
            ? 'Never ends'
            : flatFields.untilMoment,
        },
      )

      analytics?.track(
        'create budget flyout - successfully create a budget - button press',
        {
          interaction: 'button press',
          intent: 'successfully create a budget',
          screen: createBudgetUrl(),
          typeOfBudget: 'Standard',
          budgetName: flatFields.displayName,
          recipientCount:
            flatFields.emails !== undefined
              ? flatFields.emails.length
              : flatFields.email !== undefined
                ? 1
                : 0,
          recipient:
            flatFields.emails !== undefined
              ? flatFields.emails
              : flatFields.email !== undefined
                ? flatFields.email
                : undefined,
          isRecipientMe: flatFields.emails
            ? flatFields.emails.includes(user.email)
            : flatFields.email !== undefined
              ? flatFields.email === user.email
              : undefined,
          sourceAccount:
            flatFields.parentCreditCardId !== undefined
              ? creditCards[flatFields.parentCreditCardId].displayName
              : undefined,
          budgetLimit: flatFields.isUnlimited
            ? 'Unlimited'
            : '$' + flatFields.issuingLimitDollars,
          activeUntil: flatFields.isForever
            ? 'Never ends'
            : flatFields.untilMoment,
        },
      )
    }
  }

  const baseFlatFields = useMemo(
    (): BudgetFlatFormValues => ({
      displayName: props.budgetRequest.displayName,
      email: props.recipient.email,
      issuingLimitDollars: props.budgetRequest.issuingLimit,
      untilMoment: props.budgetRequest.untilTimezonedMoment,
      isUnlimited: props.budgetRequest.issuingLimitCents === undefined,
      isForever: props.budgetRequest.untilTimezonedMoment === undefined,
      currency: props.budgetRequest.currency,
      notes: props.budgetRequest.notes,
    }),
    [props.budgetRequest, props.recipient],
  )

  const baseRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> => ({
      amountDollars:
        props.budgetRequest?.recurrence?.balance ||
        props.budgetRequest.issuingLimit,
      period: props.budgetRequest.recurrence?.period,
      interval: props.budgetRequest.recurrence?.interval,
      terminator: props.budgetRequest.recurrence?.terminator,
      count:
        props.budgetRequest.recurrence?.remainingCount !== undefined
          ? props.budgetRequest.recurrence.remainingCount
          : props.budgetRequest.recurrence?.count,
      untilMoment: props.budgetRequest.recurrence?.untilTimezonedMoment(
        props.budgetRequest.timezone,
      ),
      byWeekDay: props.budgetRequest.recurrence?.byWeekDay,
      byMonthDay: props.budgetRequest.recurrence?.byMonthDay,
      byYearDay: props.budgetRequest.recurrence?.byYearDay,
    }),
    [props.budgetRequest],
  )

  const initialRecurrenceFields = useMemo(
    (): Partial<RecurrenceFormValues> => ({
      amountDollars:
        props.budgetRequest?.recurrence?.balance ||
        props.budgetRequest.issuingLimit,
      period: props.budgetRequest.recurrence?.period,
      interval: props.budgetRequest.recurrence?.interval,
      terminator: props.budgetRequest.recurrence?.terminator,
      count:
        props.budgetRequest.recurrence?.remainingCount !== undefined
          ? props.budgetRequest.recurrence.remainingCount
          : props.budgetRequest.recurrence?.count,
      untilMoment: props.budgetRequest.recurrence?.untilTimezonedMoment(
        props.budgetRequest.timezone,
      ),
      byWeekDay: props.budgetRequest.recurrence?.byWeekDay,
      byMonthDay: props.budgetRequest.recurrence?.byMonthDay,
      byYearDay: props.budgetRequest.recurrence?.byYearDay,
    }),
    [props.budgetRequest],
  )

  return (
    <ActionRightFlyoutContent
      title={translate(
        showRequestBudget
          ? 'Request Budget Update'
          : showApproveBudgetRequest
            ? 'Approve Budget Request'
            : 'Create a Budget',
      )}
      specificElements={{
        subtitle: translate('Recurring'),
        imageSrc: budgetRecurringActiveImage,
        changeLink: {
          ariaLabelSource: 'Change Budget Type',
          changeMessage: translate('Change Budget Type'),
          onChangeClick: (): void => {
            props.setScreen(BudgetScreen.Pick)
          },
        },
      }}
      permissions={{
        isBudget: true,
      }}
    >
      <BudgetForm
        buttonText={translate(
          showRequestBudget
            ? 'Request Budget Update'
            : showApproveBudgetRequest
              ? 'Approve Budget Request'
              : 'Create a Budget',
        )}
        topText={translate(
          'Requests require approval before the budget can be used. While awaiting for approval, you can update your request.',
        )}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.puttingBudgetRequest}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={
          showRequestBudget ? translate('Request from') : translate('Recipient')
        }
        recurrenceEditable
        rcnOptions={props.rcnOptions}
        initialFlatFields={props.initialFlatFields}
        initialRecurrenceFields={initialRecurrenceFields}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        updateProps={{
          baseFlatFields,
          baseRecurrenceFields: baseRecurrenceFields,
          timezone: props.budgetRequest.timezone,
        }}
        isFormDisabled={props.isFormDisabled}
        suggestedProps={{
          showSuggested: user?.hasOrgAdminPermissions,
          showOwnUserInSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.delegateCreditCardTo]}
        unableToAddNewUser={!user?.hasOrgAdminPermissions}
        isRequestBudget={showRequestBudget}
        isUpdateRequest={showRequestBudget}
        isApproveRequest={props.approving}
        approver={props.approver}
        currencyError={props.showCurrencyError}
      />
    </ActionRightFlyoutContent>
  )
}

export default UpdateBudgetRequestRecurringContent
